import { useEffect, useMemo, useState } from "react";
import {
  CircleDollarSign,
  CalendarDays,
  LayoutDashboard,
  BarChart3,
  Euro,
  Building2,
  TrendingUp,
  Wallet,
  Users,
} from "lucide-react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { Link, useLocation } from "react-router-dom";

import CeoExecutiveKpis from "@/components/pilotage/CeoExecutiveKpis";
import MonthlyObjectivesDashboard from "@/components/pilotage/MonthlyObjectivesDashboard";
import BusinessAlerts from "@/components/pilotage/BusinessAlerts";
import AdminLayout from "@/components/layout/AdminLayout";
import GlobalAdminHeader from "@/components/layout/GlobalAdminHeader";
import CollaboratorsRevenueTable from "@/components/pilotage/CollaboratorsRevenueTable";
import DashboardSection from "@/components/pilotage/DashboardSection";
import DashboardKpiInfoPopover from "@/components/pilotage/DashboardKpiInfoPopover";
import ForecastRevenueChart from "@/components/pilotage/ForecastRevenueChart";
import IntercontratPanel from "@/components/pilotage/IntercontratPanel";
import RealizedRevenueClosedPeriod from "@/components/pilotage/RealizedRevenueClosedPeriod";
import RealizedRevenueByClient from "@/components/pilotage/RealizedRevenueByClient";
import PilotagePageHeader from "@/components/pilotage/PilotagePageHeader";
import { Button } from "@/components/ui/button";
import AppErrorState from "@/components/ui/AppErrorState";
import AppEmptyState from "@/components/ui/AppEmptyState";
import DonutSplitLayout from "@/components/ui/DonutSplitLayout";
import KpiStatCard from "@/components/ui/KpiStatCard";
import { SkeletonCard } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_KPI_REGISTRY } from "@/admin/config/adminKpiNaming.ts";
import { subscribeTimesheetUpdated } from "@/lib/dataEvents";
import {
  getPilotageCockpit,
  listObjectivesByYear,
} from "@/lib/pilotageApi";
import {
  formatPilotageCount,
  formatPilotageCurrency,
  formatPilotageDecimal,
  formatPilotagePercentKpi,
  formatPilotagePercentDetail,
 
} from "@/lib/pilotageUi";
import { normalizeCollaboratorsRevenueRows } from "@/lib/pilotageCollaborators";
import { TESTIDS } from "@/lib/testids";

const CHART_COLORS = {
  primary: "#3B82F6",
  secondary: "#8B5CF6",
  clients: [
    "#0EA5E9",
    "#14B8A6",
    "#22C55E",
    "#EAB308",
    "#F97316",
    "#F43F5E",
    "#8B5CF6",
    "#6366F1",
  ],
  others: "#94A3B8",
};

const DASHBOARD_SEARCH_STORAGE_KEY = "pilotage_dashboard_search";
const DASHBOARD_ANCHORS = [
  {
    href: "#pilotage-kpis",
    label: "Pilotage exécutif",
    description: "Indicateurs clés et filtres de période propres à ce bloc.",
    testId: TESTIDS.pilotage.dashboard.stickyAnchorKpis,
    icon: LayoutDashboard,
  },
  {
    href: "#pilotage-pnl",
    label: "P&L",
    description: "Revenus, coûts et résultat mois par mois.",
    testId: TESTIDS.pilotage.dashboard.stickyAnchorPnl,
    icon: BarChart3,
  },
  {
    href: "#pilotage-realized-revenue",
    label: "CA réalisé",
    description: "Chiffre d’affaires des mois clôturés.",
    testId: "pilotage-dashboard-sticky-anchor-realized-revenue",
    icon: Euro,
  },
  {
    href: "#pilotage-realized-revenue-clients",
    label: "Clients",
    description: "Répartition du chiffre d’affaires par client.",
    testId: "pilotage-dashboard-sticky-anchor-realized-revenue-clients",
    icon: Building2,
  },
  {
    href: "#pilotage-projection",
    label: "Projections",
    description: "Prévisions et ressources attendues en fin d’année.",
    testId: TESTIDS.pilotage.dashboard.stickyAnchorProjection,
    icon: TrendingUp,
  },
  {
    href: "#pilotage-charges-fixes",
    label: "Charges fixes",
    description: "Charges récurrentes de l’exercice.",
    testId: TESTIDS.pilotage.dashboard.stickyAnchorFixedCosts,
    icon: Wallet,
  },
  {
    href: "#pilotage-collaborateurs",
    label: "Collaborateurs",
    description: "Consultants en mission, intercontrat et rentabilité.",
    testId: TESTIDS.pilotage.dashboard.stickyAnchorCollaborators,
    icon: Users,
  },
];

DASHBOARD_ANCHORS.push({ href: "#pilotage-business", label: "Analyse détaillée",
  description: "Ratios financiers, répartition et alertes.", icon: CircleDollarSign,
  testId: "pilotage-dashboard-section-business" });

DASHBOARD_ANCHORS.push({href: "#pilotage-monthly-objectives", label: "Objectifs par mois", description: "Cibles mensuelles, réalisé et écarts de chaque mois.", icon: CalendarDays, testId: "pilotage-dashboard-section-monthly-objectives"});

const toNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};
const toOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const hasNumericField = (payload, fieldName) =>
  Object.prototype.hasOwnProperty.call(payload, fieldName) &&
  Number.isFinite(Number(payload[fieldName]));

const normalizeClientDistribution = (items = []) =>
  Array.isArray(items)
    ? items
      .map((item) => ({
        client_id: String(item?.client_id || ""),
        client_name: String(item?.client_name || item?.client_id || "Client inconnu"),
        active_missions: toNumber(item?.active_missions),
      }))
      .filter((item) => item.client_id && item.active_missions > 0)
    : [];

const normalizeFixedCostsByCategory = (items = []) =>
  Array.isArray(items)
    ? items
      .map((item) => ({
        category: String(item?.category || ""),
        label: String(item?.label || item?.category || "Catégorie"),
        amount_monthly_total: toNumber(item?.amount_monthly_total),
      }))
      .filter((item) => item.category)
    : [];

const normalizeFinancials = (payload = {}) => {
  const fixedCostsMonthlyTotal = toNumber(payload.fixed_costs_monthly_total);
  const fixedCostsAnnualTotal = toNumber(payload.fixed_costs_annual_total);
  const fixedCostsMonthlyByCategory = normalizeFixedCostsByCategory(
    payload.fixed_costs_monthly_by_category,
  );

  return {
    ca_total_mensuel: toNumber(payload.ca_total_mensuel),
    ca_total_annuel: toNumber(payload.ca_total_annuel),
    benefice_total_annuel: toNumber(payload.benefice_total_annuel),
    marge_moyenne: toNumber(payload.marge_moyenne),
    missions_actives: toNumber(payload.missions_actives),
    has_missions_actives: hasNumericField(payload, "missions_actives"),
    missions_freelance: toNumber(payload.missions_freelance),
    missions_cdi: toNumber(payload.missions_cdi),
    missions_via_intermediaire: toNumber(payload.missions_via_intermediaire),
    client_distribution: normalizeClientDistribution(payload.client_distribution),
    fixed_costs_monthly_total: fixedCostsMonthlyTotal,
    fixed_costs_annual_total: fixedCostsAnnualTotal,
    benefice_total_mensuel: toNumber(payload.benefice_total_mensuel),
    mission_expenses_monthly_total: toNumber(payload.mission_expenses_monthly_total),
    mission_expenses_annual_total: toNumber(payload.mission_expenses_annual_total),
    net_profit_monthly_estime: toNumber(payload.net_profit_monthly_estime),
    net_profit_annual_estime: toNumber(payload.net_profit_annual_estime),
    margin_without_intermediary_annual_pct: toOptionalNumber(payload.margin_without_intermediary_annual_pct),
    margin_with_intermediary_monthly_pct: toOptionalNumber(payload.margin_with_intermediary_monthly_pct),
    margin_without_intermediary_monthly_pct: toOptionalNumber(payload.margin_without_intermediary_monthly_pct),
    ca_per_consultant_annual: toOptionalNumber(payload.ca_per_consultant_annual),
    fixed_costs_monthly_by_category: fixedCostsMonthlyByCategory,
  };
};

const normalizeFinancialsFromCockpit = (payload = {}) => {
  const finance = payload?.finance || {};
  const charts = payload?.charts || {};
  const missionTypeDistribution = Array.isArray(charts?.mission_type_distribution)
    ? charts.mission_type_distribution
    : [];
  const missionFreelance = missionTypeDistribution.find((item) => String(item?.type || "").toUpperCase() === "FREELANCE");
  const missionCdi = missionTypeDistribution.find((item) => String(item?.type || "").toUpperCase() === "CDI");
  const missionFreelanceCount = toNumber(missionFreelance?.value);
  const missionCdiCount = toNumber(missionCdi?.value);

  return normalizeFinancials({
    ca_total_mensuel: finance.ca_monthly_net,
    ca_total_annuel: finance.ca_annual_net,
    benefice_total_annuel: finance.mission_profit_annual,
    marge_moyenne: finance.average_margin_pct,
    missions_actives: missionFreelanceCount + missionCdiCount,
    missions_freelance: missionFreelanceCount,
    missions_cdi: missionCdiCount,
    missions_via_intermediaire: 0,
    client_distribution: charts.client_distribution,
    fixed_costs_monthly_total: finance.fixed_costs_monthly_total,
    fixed_costs_annual_total: finance.fixed_costs_annual_total,
    benefice_total_mensuel: finance.mission_profit_monthly,
    mission_expenses_monthly_total: finance.mission_expenses_monthly,
    mission_expenses_annual_total: finance.mission_expenses_annual,
    net_profit_monthly_estime: finance.net_profit_monthly,
    net_profit_annual_estime: finance.net_profit_annual,
    margin_without_intermediary_annual_pct: finance.margin_without_intermediary_annual_pct,
    margin_with_intermediary_monthly_pct: finance.margin_with_intermediary_monthly_pct,
    margin_without_intermediary_monthly_pct: finance.margin_without_intermediary_monthly_pct,
    ca_per_consultant_annual: finance.ca_per_consultant_annual,
    fixed_costs_monthly_by_category: charts.fixed_costs_monthly_by_category,
  });
};

const normalizeDashboardObjectives = (payload = []) =>
  (Array.isArray(payload) ? payload : [])
    .filter((objective) => objective?.id && objective?.name)
    .map((objective) => ({
      id: String(objective.id),
      name: String(objective.name),
      slug: String(objective.slug || ""),
      is_default: Boolean(objective.is_default),
      reconciliation: objective.reconciliation,
    }));

const normalizeObjectiveRealizedMetricsFromCockpit = (payload = {}) => ({
  tjm_target: toOptionalNumber(
    payload?.comparisons?.tjm_net?.actual ?? payload?.finance?.average_tjm_net,
  ),
  margin_target: toOptionalNumber(
    payload?.comparisons?.margin_pct?.actual ?? payload?.finance?.average_margin_pct,
  ),
  total_annual_billed_etp: toOptionalNumber(
    payload?.comparisons?.annual_billed_etp?.actual,
  ),
});

const normalizeStaffingMetricsFromCockpit = (payload = {}) => ({
  active_employees: toOptionalNumber(payload?.staffing?.active_employees),
  active_freelances: toOptionalNumber(payload?.staffing?.active_freelances),
  active_externals: toOptionalNumber(payload?.staffing?.active_externals),
  active_total: toOptionalNumber(payload?.staffing?.active_total),
  active_consultants: toOptionalNumber(payload?.staffing?.active_consultants),
  occupation_pct: toOptionalNumber(payload?.staffing?.occupation_pct),
  annual_billed_days: toOptionalNumber(
    payload?.staffing?.annual_billed_days ?? payload?.comparisons?.annual_billed_etp?.annual_billed_days,
  ),
  annual_base_days: toOptionalNumber(
    payload?.staffing?.annual_base_days ?? payload?.comparisons?.annual_billed_etp?.annual_base_days ?? 210,
  ),
});

const normalizeForecastRevenueDataFromCockpit = (payload = {}) =>
  (Array.isArray(payload?.charts?.forecast_revenue_monthly)
    ? payload.charts.forecast_revenue_monthly
    : [])
    .map((entry) => {
      const key = String(entry?.key || "");
      return {
        key,
        name: String(entry?.month || key),
        ca: toNumber(entry?.ca),
      };
    })
    .filter((entry) => entry.key);

const normalizePnlDataFromCockpit = (payload = {}) =>
  (Array.isArray(payload?.tables?.pnl) ? payload.tables.pnl : []).map(
    (entry) => ({
      key: String(entry?.key || ""),
      month: String(entry?.month || ""),
      caNet: toNumber(entry?.ca_net),
      intermediaryLoss: toNumber(entry?.intermediary_loss),
      externalConsultantCost: toNumber(entry?.external_consultant_cost),
      internalConsultantCost: toNumber(entry?.internal_consultant_cost),
      missionExpenses: toNumber(entry?.mission_expenses),
      grossMargin: toNumber(entry?.gross_margin),
      fixedCosts: toNumber(entry?.fixed_costs),
      netResult: toNumber(entry?.net_result),
    })
  );

const normalizeBusinessAlertsFromCockpit = (priorities = []) =>
  (Array.isArray(priorities) ? priorities : []).map((priority, index) => {
    const type = String(priority?.type || "").toUpperCase();
    const tone = type === "INTERCONTRAT"
      ? "danger"
      : type === "MISSIONS_ENDING_SOON"
        ? "warning"
        : "info";

    return {
      id: String(priority?.type || `priority-${index}`).toLowerCase(),
      tone,
      title: String(priority?.label || "Priorite"),
      description: String(priority?.detail || ""),
      to: String(priority?.cta_path || "/admin/pilotage/dashboard"),
      linkLabel: String(priority?.cta_label || "Voir"),
    };
  });

const normalizeIntercontratDataFromCockpit = (payload = {}) => {
  const rows = Array.isArray(payload?.intercontrat?.items)
    ? payload.intercontrat.items
      .map((row) => ({
        key: String(row?.key || ""),
        label: String(row?.label || "Collaborateur"),
        secondary: row?.secondary ? String(row.secondary) : null,
      }))
      .filter((row) => row.key)
    : [];

  const activeConsultantsCount = toNumber(payload?.staffing?.active_consultants);
  const totalIntercontrat = toNumber(payload?.intercontrat?.count, rows.length);
  const totalCollaborators = toNumber(payload?.staffing?.active_total);

  return {
    rows,
    totalCollaborators,
    activeConsultantsCount,
    totalIntercontrat,
  };
};

// Frontend recalculations removed to enforce Single Source of Truth backend values.

const formatLastUpdated = (timestamp) => {
  if (!(timestamp instanceof Date) || Number.isNaN(timestamp.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(timestamp);
};

const readStringPreference = (storageKey, fallback = "") => {
  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    return typeof rawValue === "string" ? rawValue : fallback;
  } catch {
    return fallback;
  }
};

const writeStringPreference = (storageKey, value) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, String(value ?? ""));
  } catch {
    // Ignore storage write errors to avoid breaking dashboard rendering.
  }
};

const DistributionTooltip = ({ active, payload, total }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const { name, value } = payload[0];
  const ratio = total > 0 ? (value / total) * 100 : 0;

  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/95 p-3 shadow-xl">
      <p className="text-sm font-medium text-white">{name}</p>
      <p className="text-sm text-lenvia-blue">
        {formatPilotageCount(value)} mission{value > 1 ? "s" : ""}
      </p>
      <p className="text-xs text-slate-400">{formatPilotagePercentKpi(ratio)} du total</p>
    </div>
  );
};

const ChartCard = ({ title, description, children, testId, action = null }) => (
  <DashboardSection
    title={(
      <span className="flex items-center gap-2">
        <CircleDollarSign className="h-5 w-5 text-lenvia-blue" />
        {title}
      </span>
    )}
    description={description}
    action={action}
    testId={testId}
    className="glass"
  >
    {children}
  </DashboardSection>
);


const DashboardLoadingSkeleton = () => (
  <div
    className="relative space-y-6"
    data-testid={TESTIDS.pilotage.dashboard.loadingState}
  >
    <div data-testid={TESTIDS.pilotage.dashboard.loadingSkeleton} />
    <DashboardSection
      title="KPIs financiers"
      description="Chargement des indicateurs en cours."
      className="glass"
    >
      <div className="space-y-4">
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <SkeletonCard key={`kpi-skeleton-${index}`} />
          ))}
        </div>
      </div>
    </DashboardSection>

    <DashboardSection
      title="Collaborateurs (CA & missions)"
      description="Chargement des données collaborateurs."
      className="glass"
    >
      <CollaboratorsRevenueTable
        isLoading
        showStatus={false}
        tableTestId={TESTIDS.pilotage.dashboard.consultantRevenueTable}
      />
    </DashboardSection>
  </div>
);

const DashboardContent = ({
  rawCockpitData,
  financials,
  staffingMetrics,
  forecastRevenueData,
  businessAlerts,
  intercontratData,
  isRh,
  profitabilityRows,
  objectiveRealizedMetrics,
  pnlData,
  currentYear,
  realizedRevenueYtdClosed,
  objectiveId,
  activeSection,
}) => {
  const [collaboratorsSearch, setCollaboratorsSearch] = useState(() =>
    readStringPreference(DASHBOARD_SEARCH_STORAGE_KEY, ""),
  );
  const handleCollaboratorsSearchChange = (event) => {
    setCollaboratorsSearch(event.target.value);
  };
  const resolvedIntercontratData = intercontratData || { rows: [], totalCollaborators: 0, activeConsultantsCount: 0, totalIntercontrat: 0 };
  const sharedMargin = toOptionalNumber(objectiveRealizedMetrics?.margin_target);
  const sharedTjm = toOptionalNumber(objectiveRealizedMetrics?.tjm_target);
  const sharedAnnualBilledEtp = toOptionalNumber(objectiveRealizedMetrics?.total_annual_billed_etp);
  const annualRevenueValue = financials.ca_total_annuel;
  const marginValue = sharedMargin ?? financials.marge_moyenne;
  const activeResourcesCount = toOptionalNumber(staffingMetrics?.active_total) ?? 0;
  const activeConsultantsCount = toOptionalNumber(staffingMetrics?.active_consultants) ?? 0;
  const occupationPct = toOptionalNumber(staffingMetrics?.occupation_pct) ?? (activeResourcesCount > 0 ? activeConsultantsCount / activeResourcesCount * 100 : 0);
  const caPerConsultant = toOptionalNumber(financials.ca_per_consultant_annual);
  const normalizedForecastRevenueData = Array.isArray(forecastRevenueData) ? forecastRevenueData : [];
  const normalizedPnlData = Array.isArray(pnlData) ? pnlData : [];
  const normalizedBusinessAlerts = Array.isArray(businessAlerts) ? businessAlerts : [];
  const latestCollaboratorsRevenueRows = profitabilityRows.slice(0, 5);
  const normalizedCollaboratorsSearch = collaboratorsSearch.trim().toLowerCase();
  const filteredCollaboratorsRevenueRows = useMemo(() => {
    if (!normalizedCollaboratorsSearch) {
      return latestCollaboratorsRevenueRows;
    }

    return latestCollaboratorsRevenueRows.filter((row) => {
      const searchableRow = [
        row?.clientName,
        row?.missionLabel,
        row?.consultantLabel,
        row?.consultantEmail,
      ]
        .join(" ")
        .toLowerCase();
      return searchableRow.includes(normalizedCollaboratorsSearch);
    });
  }, [latestCollaboratorsRevenueRows, normalizedCollaboratorsSearch]);
  useEffect(() => {
    writeStringPreference(DASHBOARD_SEARCH_STORAGE_KEY, collaboratorsSearch);
  }, [collaboratorsSearch]);

  const forecastData = rawCockpitData?.forecast || {};
  
  const projectedCa = forecastData.projected_ca_annual_net || 0;
  const actualCa = forecastData.actual_ca_net || 0;
  const targetCa = rawCockpitData?.objectives?.target_ca_annual_net || 0;
  const deltaCa = forecastData.delta_vs_target || 0;
  const progressCa = targetCa > 0 ? (projectedCa / targetCa) * 100 : 0;

  const projectedResources = forecastData.projected_resources || 0;
  const actualResources = forecastData.actual_resources || 0;
  const plannedEntries = forecastData.planned_entries || 0;

  const projectedTjm = forecastData.projected_tjm_net || 0;
  const actualTjm = forecastData.actual_tjm_net || 0;
  const tjmDiff = projectedTjm - actualTjm;
  const billedDays = projectedTjm > 0 ? (projectedCa / projectedTjm) : 0;
  const impactCa = tjmDiff * billedDays;

  const currentMonthNum = rawCockpitData?.context?.current_month ? parseInt(rawCockpitData.context.current_month.split("-")[1], 10) : 1;
  const showAugustPonderation = currentYear < 2027 && currentMonthNum <= 8;

  const trendStatus = (() => {
    if (!targetCa) return { label: "Stable", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/20", desc: "Aucun objectif actif défini." };
    const pct = (deltaCa / targetCa) * 100;
    if (deltaCa > 0) return { label: "En avance", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20", desc: "Le CA projeté dépasse l'objectif annuel." };
    if (pct >= -2) return { label: "Stable", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20", desc: "Le CA projeté est en phase avec l'objectif annuel." };
    if (pct >= -5) return { label: "À surveiller", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20", desc: "Le CA projeté est légèrement en retrait par rapport à l'objectif." };
    return { label: "En retard", color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/20", desc: "Le CA projeté est en retard significatif par rapport à l'objectif." };
  })();



  const getFrenchMonthName = (monthStr) => {
    if (!monthStr || !monthStr.includes("-")) return "en cours";
    const monthNum = parseInt(monthStr.split("-")[1], 10);
    const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
    return months[monthNum - 1] || "en cours";
  };
  const currentMonthStr = getFrenchMonthName(rawCockpitData?.context?.current_month);

  // ── Bloc 1 : Vue annuelle ──────────────────────────────────────────────────
  const annualCards = [
    {
      label: "CA annuel net",
      value: formatPilotageCurrency(annualRevenueValue),
      hint: "Chiffre d'affaires net annuel, après commissions intermédiaires.",
      infoNode: <DashboardKpiInfoPopover kpiKey="ca_annuel_net" />,
      dataTestId: TESTIDS.pilotage.dashboard.kpiCaAnnuel,
    },
    {
      label: "Charges fixes (année)",
      value: formatPilotageCurrency(financials.fixed_costs_annual_total),
      hint: "Charges de structure projetées sur l'année.",
      infoNode: <DashboardKpiInfoPopover kpiKey="charges_fixes_annuelles" />,
      dataTestId: TESTIDS.pilotage.dashboard.kpiFixedCostsAnnual,
    },
    ...(!isRh
      ? [
          {
            label: "Bénéfice brut missions",
            value: formatPilotageCurrency(financials.benefice_total_annuel),
            hint: "CA net moins coûts consultants, avant charges fixes.",
            infoNode: <DashboardKpiInfoPopover kpiKey="benefice_brut_missions_annuel" />,
            dataTestId: TESTIDS.pilotage.dashboard.kpiBeneficeAnnuel,
          },
          {
            label: "Résultat net (année)",
            value: formatPilotageCurrency(financials.net_profit_annual_estime),
            hint: "CA net moins coûts consultants, frais annuels et charges fixes annuelles.",
            infoNode: <DashboardKpiInfoPopover kpiKey="resultat_net_annuel" />,
            dataTestId: TESTIDS.pilotage.dashboard.kpiNetProfitAnnual,
          },
          {
            label: "Marge mission sans intermédiaire (%)",
            value: formatPilotagePercentDetail(financials.margin_without_intermediary_annual_pct),
            hint: "Marge (bénéfice brut / CA brut) calculée avant déduction des commissions intermédiaires (vision annuelle).",
            dataTestId: "pilotage-dashboard-kpi-marge-sans-intermediaire-annuel",
          },
          {
            label: "Marge mission avec intermédiaire (%)",
            value: formatPilotagePercentDetail(marginValue),
            hint: "Marge (bénéfice net / CA net) conservée après paiement des consultants et intermédiaires (vision annuelle).",
            infoNode: <DashboardKpiInfoPopover kpiKey="marge_brute_mission" />,
            dataTestId: TESTIDS.pilotage.dashboard.kpiMargeMoyenne,
          },
        ]
      : []),
  ];

  // ── Bloc 2 : Vue mensuelle ─────────────────────────────────────────────────
  const monthlyCards = [
    {
      label: `CA net estimé (${currentMonthStr})`,
      value: formatPilotageCurrency(financials.ca_total_mensuel),
      hint: currentYear >= 2027
        ? `CA net estimé pour ${currentMonthStr}, selon les jours renseignés ou le forfait de 18 jours/mois, au prorata des périodes d’activité.`
        : `CA net estimé pour le mois de ${currentMonthStr} (jours déclarés ou 18j par défaut).`,
      infoNode: <DashboardKpiInfoPopover kpiKey="ca_mensuel_net" />,
      dataTestId: TESTIDS.pilotage.dashboard.kpiCaMensuel,
    },
    {
      label: `Charges fixes (${currentMonthStr})`,
      value: formatPilotageCurrency(financials.fixed_costs_monthly_total),
      hint: `Charges de structure actives sur le mois de ${currentMonthStr}.`,
      infoNode: <DashboardKpiInfoPopover kpiKey="charges_fixes_mensuelles" />,
      dataTestId: TESTIDS.pilotage.dashboard.kpiFixedCostsMonthly,
    },
    ...(!isRh
      ? [
          {
            label: `Bénéfice brut missions (${currentMonthStr})`,
            value: formatPilotageCurrency(financials.benefice_total_mensuel),
            hint: `Bénéfice brut dégagé sur le mois de ${currentMonthStr} (CA net moins coûts d'interventions).`,
            dataTestId: "pilotage-dashboard-kpi-benefice-mensuel",
          },
          {
            label: `Résultat net (${currentMonthStr})`,
            value: formatPilotageCurrency(financials.net_profit_monthly_estime),
            hint: "Résultat net réel du mois courant, issu de la ligne P&L mensuelle.",
            infoNode: <DashboardKpiInfoPopover kpiKey="resultat_net_mensuel_lisse" />,
            dataTestId: TESTIDS.pilotage.dashboard.kpiNetProfitMonthly,
          },
          {
            label: `Marge mission sans int. (${currentMonthStr}, %)`,
            value: formatPilotagePercentDetail(financials.margin_without_intermediary_monthly_pct),
            hint: `Marge mensuelle avant déduction des commissions intermédiaires pour ${currentMonthStr}.`,
            dataTestId: "pilotage-dashboard-kpi-marge-sans-intermediaire-mensuel",
          },
          {
            label: `Marge mission avec int. (${currentMonthStr}, %)`,
            value: formatPilotagePercentDetail(financials.margin_with_intermediary_monthly_pct),
            hint: `Marge mensuelle conservée après commission intermédiaire pour ${currentMonthStr}.`,
            dataTestId: "pilotage-dashboard-kpi-marge-avec-intermediaire-mensuel",
          },
        ]
      : []),
  ];

  // ── Bloc 3 : Staffing ──────────────────────────────────────────────────────
  const staffingKpiCards = [
    {
      label: "Consultants actifs",
      value: formatPilotageCount(activeConsultantsCount),
      hint: "Consultants positionnés sur au moins une mission active.",
      infoNode: <DashboardKpiInfoPopover kpiKey="consultants_actifs" />,
      dataTestId: TESTIDS.pilotage.dashboard.kpiConsultantsActifs,
    },
    {
      label: "En intercontrat",
      value: formatPilotageCount(resolvedIntercontratData.totalIntercontrat),
      hint: "Collaborateurs actifs sans mission en cours — à repositionner.",
      infoNode: <DashboardKpiInfoPopover kpiKey="consultants_intercontrat" />,
      dataTestId: "pilotage-dashboard-kpi-intercontrat",
    },
    {
      label: "Taux d'occupation",
      value: formatPilotagePercentKpi(occupationPct),
      hint: "Consultants en mission / total collaborateurs actifs.",
      infoNode: <DashboardKpiInfoPopover kpiKey="taux_occupation" />,
      dataTestId: TESTIDS.pilotage.dashboard.kpiTauxOccupation,
    },
    ...(sharedTjm !== null
      ? [
        {
          label: "TJM net moyen",
          value: formatPilotageCurrency(sharedTjm),
          hint: "Taux journalier moyen net observé sur les missions actives.",
          infoNode: <DashboardKpiInfoPopover kpiKey="tjm_moyen" />,
          dataTestId: TESTIDS.pilotage.dashboard.kpiTjmMoyen,
        },
        ]
      : []),
    ...(sharedAnnualBilledEtp !== null
      ? [
        {
          label: currentYear > 2026 ? "ETP facturables prévisionnels" : "ETP facturés (année)",
          value: `${formatPilotageDecimal(sharedAnnualBilledEtp)} ETP`,
          hint: currentYear > 2026
            ? "Équivalents Temps Plein prévisionnels sur l'année (1 ETP = 210j)."
            : "Équivalents Temps Plein facturés sur l'année (1 ETP = 210j).",
          infoNode: (
            <DashboardKpiInfoPopover
              kpiKey="etp_factures_annuels"
              extraData={{
                year: currentYear,
                isForecast: currentYear > 2026,
                annualBilledEtp: sharedAnnualBilledEtp,
                annualBilledDays: staffingMetrics?.annual_billed_days,
                annualBaseDays: staffingMetrics?.annual_base_days || 210,
                rows: profitabilityRows,
              }}
            />
          ),
          dataTestId: "pilotage-dashboard-kpi-etp-factures-annuels",
        },
        ]
      : []),
    ...(caPerConsultant !== null
      ? [
          {
            label: "CA / consultant actif",
            value: formatPilotageCurrency(caPerConsultant),
            hint: "CA annuel net divisé par le nombre de consultants en mission.",
            infoNode: <DashboardKpiInfoPopover kpiKey="ca_par_consultant" />,
            dataTestId: TESTIDS.pilotage.dashboard.kpiCaConsultant,
          },
        ]
      : []),
  ];

  // ── Structure des missions (bloc 4) ────────────────────────────────────────
  const missionTypeCards = [
    {
      label: ADMIN_KPI_REGISTRY.missions_actives.label,
      value: formatPilotageCount(financials.missions_actives),
      hint: ADMIN_KPI_REGISTRY.missions_actives.description,
      dataTestId: TESTIDS.pilotage.dashboard.kpiMissionsActives,
    },
    {
      label: ADMIN_KPI_REGISTRY.missions_freelance.label,
      value: formatPilotageCount(financials.missions_freelance),
      hint: ADMIN_KPI_REGISTRY.missions_freelance.description,
      dataTestId: TESTIDS.pilotage.dashboard.kpiMissionsFreelance,
    },
    {
      label: ADMIN_KPI_REGISTRY.missions_cdi.label,
      value: formatPilotageCount(financials.missions_cdi),
      hint: ADMIN_KPI_REGISTRY.missions_cdi.description,
      dataTestId: TESTIDS.pilotage.dashboard.kpiMissionsCdi,
    },
  ];

  const topFixedCostCategories = [...financials.fixed_costs_monthly_by_category]
    .filter((item) => item.amount_monthly_total > 0)
    .sort(
      (left, right) =>
        right.amount_monthly_total - left.amount_monthly_total ||
        left.label.localeCompare(right.label, "fr-FR"),
    )
    .slice(0, 3);
  const hasFixedCostsMonthly = financials.fixed_costs_monthly_total > 0;
  const hasFixedCostCategoryData = topFixedCostCategories.length > 0;
  const isFixedCostCategoryDataMissing = hasFixedCostsMonthly && !hasFixedCostCategoryData;
  const simplifiedPnlRows = [
    {
      label: "CA net estimé",
      monthly: financials.ca_total_mensuel,
      annual: annualRevenueValue,
    },
    {
      label: "Frais",
      monthly: financials.mission_expenses_monthly_total,
      annual: financials.mission_expenses_annual_total,
    },
    {
      label: ADMIN_KPI_REGISTRY.charges_fixes.label,
      monthly: financials.fixed_costs_monthly_total,
      annual: financials.fixed_costs_annual_total,
    },
    {
      label: "Profit net",
      monthly: financials.net_profit_monthly_estime,
      annual: financials.net_profit_annual_estime,
    },
  ];

  // structureCards remplacé par missionTypeCards déclaré plus haut (sans missions_via_intermediaire — toujours à 0)

  const contractChartData = [
    {
      name: "Freelance",
      value: financials.missions_freelance,
      color: CHART_COLORS.primary,
    },
    {
      name: "CDI",
      value: financials.missions_cdi,
      color: CHART_COLORS.secondary,
    },
  ].filter((item) => item.value > 0);

  const contractTotal = contractChartData.reduce((sum, item) => sum + item.value, 0);
  const sortedClientDistribution = [...financials.client_distribution].sort(
    (left, right) =>
      right.active_missions - left.active_missions ||
      left.client_name.localeCompare(right.client_name, "fr-FR"),
  );
  const clientChartData = sortedClientDistribution
    .slice(0, 8)
    .map((item, index) => ({
      name: item.client_name,
      value: item.active_missions,
      color: CHART_COLORS.clients[index % CHART_COLORS.clients.length],
    }));
  const remainingClientTotal = sortedClientDistribution
    .slice(8)
    .reduce((sum, item) => sum + item.active_missions, 0);

  if (remainingClientTotal > 0) {
    clientChartData.push({
      name: "Autres",
      value: remainingClientTotal,
      color: CHART_COLORS.others,
    });
  }

  const clientTotal = clientChartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div role="tabpanel" id={`${activeSection}-panel`} aria-labelledby={`${activeSection}-tab`} tabIndex={0} className="relative min-w-0 space-y-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-lenvia-blue" data-testid={TESTIDS.pilotage.dashboard.container}>
      {activeSection === "pilotage-monthly-objectives" && (
        <MonthlyObjectivesDashboard
          key={currentYear}
          year={currentYear}
          objectiveId={objectiveId}
          isRh={isRh}
          cockpitData={rawCockpitData}
        />
      )}
      {activeSection === "pilotage-kpis" && (<div id="pilotage-kpis" className="scroll-mt-24">
        <CeoExecutiveKpis
          comparisons={rawCockpitData?.comparisons}
          finance={rawCockpitData?.finance}
          activeResourcesCount={activeResourcesCount}
          objectiveId={objectiveId}
          globalYear={currentYear}
        />
      </div>)}

      {activeSection === "pilotage-projection" && (<div id="pilotage-projection" className="scroll-mt-24 mt-8">
        <DashboardSection
          title="PROJECTION TENDANCIELLE"
          description="Tendance fin d’année basée sur le rythme actuel, les missions en cours et les démarrages planifiés."
          testId="pilotage-dashboard-projection-section"
          className="glass"
          titleClassName="text-lenvia-blue text-xl font-bold"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Carte 1 — CA annuel net projeté */}
            <div 
              className="flex h-full min-w-0 flex-col rounded-xl border border-white/10 bg-white/[0.04] p-5 shadow-sm shadow-black/20 group relative transition-all duration-300 hover:border-white/20"
              data-testid="pilotage-dashboard-kpi-projected-ca"
            >
              <div className="flex min-w-0 items-start justify-between gap-3">
                <p className="min-w-0 flex-1 break-words text-[11px] font-semibold uppercase tracking-[0.18em] leading-relaxed text-slate-500">
                  CA annuel net projeté
                </p>
              </div>
              <div className="flex items-baseline gap-2 break-words text-xl font-bold leading-tight tabular-nums text-white sm:text-2xl 2xl:text-[2rem] mt-2 mb-4">
                {formatPilotageCurrency(projectedCa)}
              </div>
              <div className="mt-auto space-y-2 text-xs border-t border-white/5 pt-3">
                <div className="flex justify-between text-slate-400">
                  <span>Réalisé actuel :</span>
                  <span className="font-semibold text-white tabular-nums">{formatPilotageCurrency(actualCa)}</span>
                </div>
                {targetCa > 0 && (
                  <>
                    <div className="flex justify-between text-slate-400">
                      <span>Objectif :</span>
                      <span className="font-semibold text-white tabular-nums">{formatPilotageCurrency(targetCa)}</span>
                    </div>
                    <div className="flex justify-between text-slate-400">
                      <span>Écart objectif :</span>
                      <span className={`font-semibold tabular-nums ${deltaCa >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {deltaCa >= 0 ? "+" : ""}{formatPilotageCurrency(deltaCa)}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-lenvia-blue bg-lenvia-blue/10 w-fit px-2 py-0.5 rounded-md">
                      🎯 {formatPilotagePercentKpi(progressCa)} obj.
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Carte 2 — Ressources projetées fin d’année */}
            <div 
              className="flex h-full min-w-0 flex-col rounded-xl border border-white/10 bg-white/[0.04] p-5 shadow-sm shadow-black/20 group relative transition-all duration-300 hover:border-white/20"
              data-testid="pilotage-dashboard-kpi-projected-resources"
            >
              <div className="flex min-w-0 items-start justify-between gap-3">
                <p className="min-w-0 flex-1 break-words text-[11px] font-semibold uppercase tracking-[0.18em] leading-relaxed text-slate-500">
                  Effectif total projeté fin d'année
                </p>
              </div>
              <div className="flex items-baseline gap-2 break-words text-xl font-bold leading-tight tabular-nums text-white sm:text-2xl 2xl:text-[2rem] mt-2 mb-4">
                {formatPilotageCount(projectedResources)}
              </div>
              <div className="mt-auto space-y-2 text-xs border-t border-white/5 pt-3">
                <div className="flex justify-between text-slate-400">
                  <span>Actuelles :</span>
                  <span className="font-semibold text-white tabular-nums">{formatPilotageCount(actualResources)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Entrées projetées :</span>
                  <span className="font-semibold text-white tabular-nums">
                    {plannedEntries >= 0 ? "+" : ""}{formatPilotageCount(plannedEntries)}
                  </span>
                </div>
                {showAugustPonderation && (
                  <div className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-amber-400/90 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-md w-fit">
                    Août pondéré : 50 %
                  </div>
                )}
              </div>
            </div>

            {/* Carte 3 — TJM projeté fin d’année */}
            <div 
              className="flex h-full min-w-0 flex-col rounded-xl border border-white/10 bg-white/[0.04] p-5 shadow-sm shadow-black/20 group relative transition-all duration-300 hover:border-white/20"
              data-testid="pilotage-dashboard-kpi-projected-tjm"
            >
              <div className="flex min-w-0 items-start justify-between gap-3">
                <p className="min-w-0 flex-1 break-words text-[11px] font-semibold uppercase tracking-[0.18em] leading-relaxed text-slate-500">
                  TJM projeté fin d'année
                </p>
              </div>
              <div className="flex items-baseline gap-2 break-words text-xl font-bold leading-tight tabular-nums text-white sm:text-2xl 2xl:text-[2rem] mt-2 mb-4">
                {formatPilotageCurrency(projectedTjm)}
              </div>
              <div className="mt-auto space-y-2 text-xs border-t border-white/5 pt-3">
                <div className="flex justify-between text-slate-400">
                  <span>TJM actuel :</span>
                  <span className="font-semibold text-white tabular-nums">{formatPilotageCurrency(actualTjm)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Évolution :</span>
                  <span className={`font-semibold tabular-nums ${tjmDiff >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {tjmDiff >= 0 ? "+" : ""}{formatPilotageCurrency(tjmDiff)}
                  </span>
                </div>
                 <div className="flex justify-between text-slate-400">
                  <span>Impact CA annuel :</span>
                  <span className={`font-semibold tabular-nums ${impactCa >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {impactCa >= 0 ? "+" : ""}{formatPilotageCurrency(impactCa)}
                  </span>
                </div>
              </div>
            </div>

            {/* Carte 4 — Synthèse tendance */}
            <div 
              className="flex h-full min-w-0 flex-col rounded-xl border border-white/10 bg-white/[0.04] p-5 shadow-sm shadow-black/20 group relative transition-all duration-300 hover:border-white/20"
              data-testid="pilotage-dashboard-kpi-trend-synthesis"
            >
              <div className="flex min-w-0 items-start justify-between gap-3">
                <p className="min-w-0 flex-1 break-words text-[11px] font-semibold uppercase tracking-[0.18em] leading-relaxed text-slate-500">
                  Synthèse tendance
                </p>
              </div>
              <div className="mt-2 mb-4 flex-1">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold uppercase tracking-wider ${trendStatus.color} ${trendStatus.bg} ${trendStatus.border}`}>
                  {trendStatus.label}
                </span>
                <p className="mt-3 text-xs text-slate-400 leading-relaxed">
                  {trendStatus.desc}
                </p>
              </div>
              <div className="mt-auto pt-3 border-t border-white/5 text-[10px] text-slate-500 italic">
                Basé sur l’écart CA projeté vs objectif.
              </div>
            </div>
          </div>
        </DashboardSection>
      </div>)}

      {activeSection === "pilotage-pnl" && (<div id="pilotage-pnl" className="scroll-mt-24 mt-8">
        <DashboardSection
          title="P&L SIMPLIFIÉ"
          description="Comparatif CA net, frais, charges fixes structure et profit net."
          testId={TESTIDS.pilotage.dashboard.pnlSimplified}
          className="glass"
          titleClassName="text-lenvia-blue text-xl font-bold"
        >
          <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="min-w-0 rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-white">{currentMonthStr.toUpperCase()} {currentYear}</p>
              <div className="mt-3 space-y-3">
                {simplifiedPnlRows.map((row) => (
                  <div
                    key={`${row.label}-monthly`}
                    className="flex min-w-0 items-center justify-between gap-3 border-b border-white/5 pb-3 last:border-none last:pb-0"
                  >
                    <span className="text-sm text-slate-300">{row.label}</span>
                    <span className="min-w-0 text-right text-sm font-semibold tabular-nums text-white [overflow-wrap:anywhere]">
                      {formatPilotageCurrency(row.monthly)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="min-w-0 rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-white">ANNÉE {currentYear}</p>
              <div className="mt-3 space-y-3">
                {simplifiedPnlRows.map((row) => (
                  <div
                    key={`${row.label}-annual`}
                    className="flex min-w-0 items-center justify-between gap-3 border-b border-white/5 pb-3 last:border-none last:pb-0"
                  >
                    <span className="text-sm text-slate-300">{row.label}</span>
                    <span className="min-w-0 text-right text-sm font-semibold tabular-nums text-white [overflow-wrap:anywhere]">
                      {formatPilotageCurrency(row.annual)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {normalizedPnlData.length > 0 && !isRh && (
            <div className="mt-6 min-w-0 rounded-xl border border-white/10 bg-white/[0.03]">
              <div className="w-full min-w-0 max-w-full overflow-x-auto [-webkit-overflow-scrolling:touch]">
                <table className="w-full min-w-[860px] text-left text-sm text-slate-300">
                  <thead className="border-b border-white/5 bg-white/[0.02] text-xs uppercase tracking-[0.16em] text-slate-500">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Mois</th>
                      <th className="px-4 py-3 text-right font-semibold">CA net estimé (€)</th>
                      <th className="px-4 py-3 text-right font-semibold">Coûts ext. (€)</th>
                      <th className="px-4 py-3 text-right font-semibold">Coûts int. (€)</th>
                      <th className="px-4 py-3 text-right font-semibold">Frais (€)</th>
                      <th className="px-4 py-3 text-right font-semibold">Marge brute (€)</th>
                      <th className="px-4 py-3 text-right font-semibold">Charges fixes (€)</th>
                      <th className="px-4 py-3 text-right font-semibold text-lenvia-blue">Résultat net (€)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {normalizedPnlData.map((row) => (
                      <tr
                        key={`pnl-${row.key}`}
                        className="transition-colors hover:bg-white/[0.02]"
                      >
                        <td className="px-4 py-3 font-bold text-white uppercase">
                          {row.month}
                          <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-500">
                            {row.key.split("-")[0]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-emerald-400">
                          {formatPilotageCurrency(row.caNet)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-400">
                          {formatPilotageCurrency(row.externalConsultantCost)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-400">
                          {formatPilotageCurrency(row.internalConsultantCost)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-400">
                          {formatPilotageCurrency(row.missionExpenses)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium text-white">
                          {formatPilotageCurrency(row.grossMargin)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-amber-400/80">
                          {formatPilotageCurrency(row.fixedCosts)}
                        </td>
                        <td className={`px-4 py-3 text-right tabular-nums font-semibold ${row.netResult >= 0 ? "text-lenvia-blue" : "text-rose-400"}`}>
                          {formatPilotageCurrency(row.netResult)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DashboardSection>
      </div>)}

      {activeSection === "pilotage-realized-revenue" && (<div id="pilotage-realized-revenue" className="scroll-mt-24 mt-8">
        <DashboardSection
          title={
            realizedRevenueYtdClosed?.period_end_label ? (
              <>
                CA RÉALISÉ — CUMUL À FIN <span className="text-lenvia-blue font-bold">{realizedRevenueYtdClosed.period_end_label.toUpperCase()}</span>
              </>
            ) : (
              "CA RÉALISÉ — CUMUL"
            )
          }
          description="Données arrêtées au dernier mois clôturé."
          testId="pilotage-dashboard-realized-revenue-section"
          className="glass"
          titleClassName="text-lenvia-blue text-xl font-bold"
        >
          {realizedRevenueYtdClosed && realizedRevenueYtdClosed.monthly && realizedRevenueYtdClosed.monthly.length > 0 ? (
            <RealizedRevenueClosedPeriod data={realizedRevenueYtdClosed} />
          ) : (
            <AppEmptyState
              title="Aucune donnée clôturée disponible pour l’année en cours."
              description="Aucune donnée de CA réalisé clôturé disponible pour le moment."
              className="p-6"
              testId="pilotage-realized-revenue-empty"
            />
          )}
        </DashboardSection>
      </div>)}

      {activeSection === "pilotage-realized-revenue-clients" && (<div id="pilotage-realized-revenue-clients" className="scroll-mt-24 mt-8">
        <DashboardSection
          title={
            realizedRevenueYtdClosed?.period_end_label ? (
              <>
                CA RÉALISÉ PAR CLIENT — CUMUL À FIN <span className="text-lenvia-blue font-bold">{realizedRevenueYtdClosed.period_end_label.toUpperCase()}</span>
              </>
            ) : (
              `CLIENTS — PRÉVISIONS ${currentYear}`
            )
          }
          description={realizedRevenueYtdClosed?.period_end_label ? "Analyse du chiffre d’affaires réalisé par client, arrêtée au dernier mois clôturé." : `Estimations ${currentYear} par client. Aucun chiffre d’affaires réalisé clôturé n’est encore disponible pour cette année.`}
          testId="pilotage-dashboard-realized-revenue-by-client-section"
          className="glass"
          titleClassName="text-lenvia-blue text-xl font-bold"
        >
          {realizedRevenueYtdClosed && realizedRevenueYtdClosed.by_client && realizedRevenueYtdClosed.by_client.length > 0 ? (
            <RealizedRevenueByClient data={realizedRevenueYtdClosed} year={currentYear} />
          ) : (
            <AppEmptyState
              title="Aucune donnée clôturée disponible pour l’année en cours."
              description="Aucune donnée de CA réalisé clôturé disponible pour le moment."
              className="p-6"
              testId="pilotage-realized-revenue-by-client-empty"
            />
          )}
        </DashboardSection>
      </div>)}

      {activeSection === "pilotage-business" && (<DashboardSection
        title="KPI BUSINESS"
        description="Indicateurs financiers détaillés annuels et mensuels, structure des missions et alertes."
        className="glass mt-8"
        titleClassName="text-lenvia-blue text-xl font-bold"
      >
        <div className="space-y-8">
          <div>
            <h3 className="mb-4 text-lg font-bold text-white uppercase tracking-wider">Vue Annuelle</h3>
            <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3 [&_.whitespace-nowrap]:whitespace-normal">
              {annualCards.map((card) => (
                <KpiStatCard key={card.label} {...card} />
              ))}
            </div>
          </div>
          
          <hr className="border-white/10" />

          <div>
            <h3 className="mb-4 text-lg font-bold text-white uppercase tracking-wider">Vue Mensuelle - {currentMonthStr.toUpperCase()} {currentYear}</h3>
            <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3 [&_.whitespace-nowrap]:whitespace-normal">
              {monthlyCards.map((card) => (
                <KpiStatCard key={card.label} {...card} />
              ))}
            </div>
          </div>

          <hr className="border-white/10" />

          <div>
            <h3 className="mb-4 text-lg font-bold text-white uppercase tracking-wider">Détail & Analyse</h3>
            <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 [&_.whitespace-nowrap]:whitespace-normal mb-6">
              {missionTypeCards.map((card) => (
                <KpiStatCard key={card.label} {...card} />
              ))}
            </div>

            <div className="grid min-w-0 gap-6 xl:grid-cols-2 mb-6">
              <ForecastRevenueChart data={normalizedForecastRevenueData} />
              <BusinessAlerts alerts={normalizedBusinessAlerts} />
            </div>

            <div className="grid min-w-0 gap-6 xl:grid-cols-2">
              <ChartCard
                title="Répartition Freelance vs CDI"
                description="Part des missions actives selon le type de contrat."
                testId={TESTIDS.pilotage.dashboard.chartContracts}
              >
                {contractChartData.length > 0 ? (
                  <DonutSplitLayout
                    figureTestId={`${TESTIDS.pilotage.dashboard.chartContracts}-figure`}
                    legendTestId={`${TESTIDS.pilotage.dashboard.chartContracts}-legend`}
                    className="w-full min-w-0 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]"
                    chartClassName="mx-auto h-[250px] w-full max-w-[250px] sm:h-[295px] sm:max-w-[295px]"
                    legendClassName="w-full min-w-0 space-y-2 lg:max-h-[295px] lg:max-w-[320px]"
                    chart={(
                      <>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={contractChartData}
                              dataKey="value"
                              nameKey="name"
                              innerRadius="58%"
                              outerRadius="82%"
                              paddingAngle={4}
                            >
                              {contractChartData.map((entry) => (
                                <Cell
                                  key={`${entry.name}-${entry.value}`}
                                  fill={entry.color}
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              content={(tooltipProps) => (
                                <DistributionTooltip {...tooltipProps} total={contractTotal} />
                              )}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-3xl font-semibold text-white">
                              {formatPilotageCount(contractTotal)}
                            </span>
                          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Missions
                          </span>
                        </div>
                      </>
                    )}
                    legend={contractChartData.map((entry) => (
                      <div
                        key={`${entry.name}-legend`}
                        className="flex min-w-0 items-start gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2"
                      >
                        <span
                          className="mt-1 h-2.5 w-2.5 flex-none rounded-full"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span
                          className="min-w-0 flex-1 truncate text-xs leading-snug text-slate-300"
                          title={entry.name}
                        >
                          {entry.name}
                        </span>
                        <span className="flex-none rounded-md bg-white/[0.04] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-white">
                          {formatPilotageCount(entry.value)}
                        </span>
                      </div>
                    ))}
                  />
                ) : (
                  <div className="flex h-[260px] items-center justify-center rounded-xl border border-dashed border-white/10 text-sm text-slate-500">
                    Aucune donnée disponible.
                  </div>
                )}
              </ChartCard>

              <ChartCard
                title="Répartition par entreprise (clients)"
                description="Nombre de missions actives par client, avec regroupement des plus petits volumes."
                testId={TESTIDS.pilotage.dashboard.clientChart}
              >
                {clientChartData.length > 0 ? (
                  <div className="w-full min-w-0">
                    <DonutSplitLayout
                      figureTestId={`${TESTIDS.pilotage.dashboard.clientChart}-figure`}
                      legendTestId={`${TESTIDS.pilotage.dashboard.clientChart}-legend`}
                      className="w-full min-w-0 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]"
                      chartClassName="mx-auto h-[250px] w-full min-w-0 max-w-[250px] sm:h-[295px] sm:max-w-[295px]"
                      legendClassName="w-full min-w-0 max-h-[310px] space-y-2 overflow-y-auto pr-1 lg:max-w-[330px]"
                      legend={clientChartData.map((entry) => (
                        <div
                          key={`${entry.name}-clients-legend`}
                          className="flex w-full min-w-0 max-w-full items-start gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2"
                          data-testid={`${TESTIDS.pilotage.dashboard.chartClients}-legend-item`}
                        >
                          <span
                            className="mt-1 h-2.5 w-2.5 flex-none rounded-full"
                            style={{ backgroundColor: entry.color }}
                          />
                          <span
                            className="min-w-0 flex-1 break-words text-xs leading-snug text-slate-300 line-clamp-2"
                            title={entry.name}
                          >
                            {entry.name}
                          </span>
                          <span className="flex-none rounded-md bg-white/[0.04] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-white">
                            {formatPilotageCount(entry.value)}
                          </span>
                        </div>
                      ))}
                      chart={(
                        <>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={clientChartData}
                                dataKey="value"
                                nameKey="name"
                                innerRadius="58%"
                                outerRadius="82%"
                                paddingAngle={3}
                              >
                                {clientChartData.map((entry) => (
                                  <Cell
                                    key={`${entry.name}-${entry.value}`}
                                    fill={entry.color}
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                content={(tooltipProps) => (
                                  <DistributionTooltip {...tooltipProps} total={clientTotal} />
                                )}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-3xl font-semibold text-white">
                              {formatPilotageCount(clientTotal)}
                            </span>
                            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                              Missions
                            </span>
                          </div>
                        </>
                      )}
                    />
                  </div>
                ) : (
                  <div className="flex h-[260px] items-center justify-center rounded-xl border border-dashed border-white/10 text-sm text-slate-500">
                    Aucune donnée disponible.
                  </div>
                )}
              </ChartCard>
            </div>
          </div>
        </div>
      </DashboardSection>)}

      {activeSection === "pilotage-charges-fixes" && (<div id="pilotage-charges-fixes" className="scroll-mt-24 mt-8">
        <DashboardSection
          title="CHARGES FIXES"
          description="Les 3 catégories de charges fixes les plus élevées sur le mois courant."
          className="glass"
          titleClassName="text-lenvia-blue text-xl font-bold"
        >
          <ChartCard
            title={`Top catégories charges (${currentMonthStr.toUpperCase()})`}
            description=""
            testId={TESTIDS.pilotage.dashboard.fixedCostsTopCategories}
          >
            {hasFixedCostCategoryData ? (
              <div className="space-y-3">
                {topFixedCostCategories.map((item, index) => (
                  <div
                    key={`${item.category}-${item.amount_monthly_total}`}
                    className="flex min-w-0 items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white">
                        {index + 1}. {item.label}
                      </p>
                      <p className="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500">
                        {item.category}
                      </p>
                    </div>
                    <p className="min-w-0 text-right text-sm font-semibold text-lenvia-blue [overflow-wrap:anywhere]">
                      {formatPilotageCurrency(item.amount_monthly_total)}
                    </p>
                  </div>
                ))}
              </div>
            ) : isFixedCostCategoryDataMissing ? (
              <div className="flex h-[180px] flex-col items-center justify-center gap-3 rounded-xl border border-amber-300/30 bg-amber-500/10 px-4 text-center text-sm text-amber-100">
                <p>
                  Données catégories indisponibles pour le mois courant.
                </p>
                <p className="text-xs text-amber-200/90">
                  `fixed_costs_monthly_total` est non nul: vérifier l'agrégation backend.
                </p>
              </div>
            ) : (
              <div className="flex h-[180px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-white/10 px-4 text-center text-sm text-slate-400">
                <p>Aucune charge fixe applicable sur le mois courant.</p>
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
                >
                  <Link to="/admin/pilotage/charges-fixes">
                    Ajouter une charge fixe
                  </Link>
                </Button>
              </div>
            )}
          </ChartCard>
        </DashboardSection>
      </div>)}

      {activeSection === "pilotage-collaborateurs" && (<div id="pilotage-collaborateurs" className="scroll-mt-24 mt-8">
        <DashboardSection
          title="COLLABORATEURS"
          description="Staffing, occupation, intercontrat et rentabilité par consultant."
          className="glass"
          titleClassName="text-lenvia-blue text-xl font-bold"
        >
          <div className="space-y-6">
            <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 [&_.whitespace-nowrap]:whitespace-normal">
              {staffingKpiCards.map((card) => (
                <KpiStatCard key={card.label} {...card} />
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-5 rounded-2xl border border-white/5 bg-white/[0.02] glass-light">
              <div className="flex flex-col">
                <span className="text-xs font-semibold uppercase text-slate-500">Salariés actifs</span>
                <span className="text-2xl font-bold text-white mt-1" data-testid="dashboard-kpi-active-employees">
                  {formatPilotageCount(staffingMetrics?.active_employees)}
                </span>
                <span className="text-[10px] text-slate-500 mt-1">CDI, CDD, Alternants</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-semibold uppercase text-slate-500">Freelances actifs</span>
                <span className="text-2xl font-bold text-white mt-1" data-testid="dashboard-kpi-active-freelances">
                  {formatPilotageCount(staffingMetrics?.active_freelances)}
                </span>
                <span className="text-[10px] text-slate-500 mt-1">Indépendants directs</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-semibold uppercase text-slate-500">Ressources externes actives</span>
                <span className="text-2xl font-bold text-white mt-1" data-testid="dashboard-kpi-active-externals">
                  {formatPilotageCount(staffingMetrics?.active_externals)}
                </span>
                <span className="text-[10px] text-slate-500 mt-1">Portage, Sous-traitance</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-semibold uppercase text-slate-500">Total ressources actives</span>
                <span className="text-2xl font-bold text-white mt-1" data-testid="dashboard-kpi-active-total">
                  {formatPilotageCount(activeResourcesCount)}
                </span>
                <span className="text-[10px] text-slate-500 mt-1">Effectif global actif</span>
              </div>
            </div>

            <IntercontratPanel {...resolvedIntercontratData} />

            <ChartCard
              title="Collaborateurs (CA & missions)"
              description={`Exercice ${currentYear} uniquement. Frais ponctuels imputés à cette année et frais récurrents encore ouverts. Les frais ponctuels d’une autre année sont exclus.`}
              testId={TESTIDS.pilotage.dashboard.collaboratorsRevenueTable}
              action={(
                <Button asChild size="sm" variant="outline" className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white">
                  <Link
                    to={`/admin/pilotage/collaborateurs/revenue?year=${currentYear}`}
                    data-testid={TESTIDS.pilotage.dashboard.collaboratorsRevenueViewMore}
                  >
                    Voir plus
                  </Link>
                </Button>
              )}
            >
              <div className="mb-4 flex min-w-0 flex-col gap-2 sm:max-w-sm">
                <label
                  htmlFor="pilotage-collaborators-search"
                  className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500"
                >
                  Rechercher (client/consultant)
                </label>
                <input
                  id="pilotage-collaborators-search"
                  type="search"
                  value={collaboratorsSearch}
                  onChange={handleCollaboratorsSearchChange}
                  onInput={handleCollaboratorsSearchChange}
                  placeholder="Ex: Client Beta, Alice..."
                  className="h-10 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 text-sm text-slate-200 outline-none transition-colors placeholder:text-slate-500 focus:border-lenvia-blue/60 focus:ring-2 focus:ring-lenvia-blue/20"
                  data-testid={TESTIDS.pilotage.dashboard.collaboratorsSearchInput}
                />
              </div>
              <CollaboratorsRevenueTable
                year={currentYear}
                rows={filteredCollaboratorsRevenueRows}
                tableTestId={TESTIDS.pilotage.dashboard.consultantRevenueTable}
                showStatus={false}
              />
            </ChartCard>
          </div>
        </DashboardSection>
      </div>)}
    </div>
  );
};

const PilotageDashboardPage = () => {
  const { role } = useAuth();
  const location = useLocation();
  const hideGlobalHeader =
    location.pathname === "/admin/pilotage" ||
    location.pathname === "/admin/pilotage/dashboard";
  const [financials, setFinancials] = useState(null);
  const [staffingMetrics, setStaffingMetrics] = useState(null);
  const [objectiveRealizedMetrics, setObjectiveRealizedMetrics] = useState(null);
  const [missions, setMissions] = useState(null);
  const [collaborators, setCollaborators] = useState(null);
  const [profitabilityRows, setProfitabilityRows] = useState([]);
  const [forecastRevenueData, setForecastRevenueData] = useState([]);
  const [pnlData, setPnlData] = useState([]);
  const [businessAlerts, setBusinessAlerts] = useState([]);
  const [intercontratData, setIntercontratData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [rawCockpitData, setRawCockpitData] = useState(null);
  const [realizedRevenueYtdClosed, setRealizedRevenueYtdClosed] = useState(null);
  const [requestKey, setRequestKey] = useState(0);
  const [currentYearObjectives, setCurrentYearObjectives] = useState([]);
  const [activeObjectiveId, setActiveObjectiveId] = useState("");
  const isRh = role === "RH";
  const [currentYear, setCurrentYear] = useState(() => {
    const requested = Number(new URLSearchParams(window.location.search).get("year"));
    return [2026, 2027].includes(requested) ? requested : Math.min(2027, Math.max(2026, new Date().getFullYear()));
  });
  useEffect(() => {
    const updateYear = () => {
      const requested = Number(new URLSearchParams(window.location.search).get("year"));
      if ([2026, 2027].includes(requested) && requested !== currentYear) {
        setActiveObjectiveId("");
        setCurrentYear(requested);
      }
    };
    window.addEventListener("pilotage-year-changed", updateYear);
    window.addEventListener("popstate", updateYear);
    return () => {
      window.removeEventListener("pilotage-year-changed", updateYear);
      window.removeEventListener("popstate", updateYear);
    };
  }, [currentYear]);
  const activeObjective = useMemo(
    () =>
      currentYearObjectives.find((objective) => objective.id === activeObjectiveId) ||
      currentYearObjectives.find((objective) => objective.is_default) ||
      currentYearObjectives[0] ||
      null,
    [currentYearObjectives, activeObjectiveId],
  );

  const [activeSection, setActiveSection] = useState(() => {
    const id = window.location.hash.slice(1);
    return DASHBOARD_ANCHORS.some(item => item.href === `#${id}`) ? id : "pilotage-kpis";
  });
  const selectSection = (id) => {
    setActiveSection(id);
    window.history.pushState(null, "", `${window.location.pathname}${window.location.search}#${id}`);
  };
  useEffect(() => {
    const restore = () => {
      const id = window.location.hash.slice(1);
      setActiveSection(DASHBOARD_ANCHORS.some(item => item.href === `#${id}`) ? id : "pilotage-kpis");
    };
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, []);
  const changeYear = (year) => {
    const params = new URLSearchParams(window.location.search);
    params.set("year", String(year));
    ["executive_year", "start", "end"].forEach(key => params.delete(key));
    window.history.replaceState(null, "", `${window.location.pathname}?${params}${window.location.hash}`);
    setActiveObjectiveId("");
    setCurrentYearObjectives([]);
    setCurrentYear(year);
  };

  useEffect(() => {
    let ignore = false;

    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const [cockpitResponse, objectivesResponse] = await Promise.all([
          getPilotageCockpit({
            year: currentYear,
            objective_id: activeObjectiveId || undefined,
          }),
          listObjectivesByYear(currentYear),
        ]);
        const cockpitTables = cockpitResponse?.tables || {};
        const normalizedObjectives = normalizeDashboardObjectives(objectivesResponse);
        const cockpitMissions = Array.isArray(cockpitTables?.missions) ? cockpitTables.missions : [];
        const cockpitCollaborators = Array.isArray(cockpitTables?.collaborators) ? cockpitTables.collaborators : [];
        if (!ignore) {
          setRawCockpitData(cockpitResponse);
          setRealizedRevenueYtdClosed(cockpitResponse?.realized_revenue_ytd_closed || null);
          setFinancials(normalizeFinancialsFromCockpit(cockpitResponse));
          setStaffingMetrics(normalizeStaffingMetricsFromCockpit(cockpitResponse));
          setObjectiveRealizedMetrics(normalizeObjectiveRealizedMetricsFromCockpit(cockpitResponse));
          setMissions(cockpitMissions);
          setCollaborators(cockpitCollaborators);
          setProfitabilityRows(normalizeCollaboratorsRevenueRows(cockpitTables?.collaborators_revenue));
          setForecastRevenueData(normalizeForecastRevenueDataFromCockpit(cockpitResponse));
          setPnlData(normalizePnlDataFromCockpit(cockpitResponse));
          setBusinessAlerts(normalizeBusinessAlertsFromCockpit(cockpitResponse?.priorities));
          setIntercontratData(
            normalizeIntercontratDataFromCockpit(
              cockpitResponse
            ),
          );
          setLastUpdatedAt(new Date());
          setCurrentYearObjectives(normalizedObjectives);
          setActiveObjectiveId((currentId) => {
            if (!currentId) {
              return "";
            }
            return normalizedObjectives.some((objective) => objective.id === currentId)
              ? currentId
              : "";
          });
        }
      } catch {
        if (!ignore) {
          setError(
            "Erreur lors du chargement du dashboard Pilotage",
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    void loadDashboard();

    return () => {
      ignore = true;
    };
  }, [activeObjectiveId, requestKey, currentYear]);

  useEffect(() => {
    const unsubscribe = subscribeTimesheetUpdated(() => {
      setRequestKey((current) => current + 1);
    });

    const refresh = () => setRequestKey((current) => current + 1);
    window.addEventListener("pilotage-data-changed", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      unsubscribe();
      window.removeEventListener("pilotage-data-changed", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

    financials?.has_missions_actives
      ? `${formatPilotageCount(financials.missions_actives)} ${financials.missions_actives === 1 ? "active" : "actives"}`
      : null;
  const handleRefresh = () => {
    setRequestKey((current) => current + 1);
  };


  return (
    <AdminLayout
      header={!hideGlobalHeader ? <GlobalAdminHeader /> : null}
      pageHeader={(
        <PilotagePageHeader
          currentYear={currentYear}
          objectives={currentYearObjectives}
          activeObjectiveId={activeObjective?.id || ""}
          onActiveObjectiveChange={setActiveObjectiveId}
          onYearChange={changeYear}
        />
      )}
    >
      <div className="flex w-full min-w-0 max-w-full flex-col gap-6" data-testid={TESTIDS.pilotage.dashboard.page}>
        {!loading && currentYear >= 2027 && !activeObjective && (
          <p className="rounded-xl border border-white/10 bg-slate-900/60 p-4 text-sm text-slate-300" role="status">
            Prévisions opérationnelles {currentYear} calculées automatiquement. Aucun scénario budgétaire renseigné pour cette année.
            {" "}<Link className="text-lenvia-blue underline" to={`/admin/pilotage/objectifs?year=${currentYear}`}>Renseigner un scénario</Link>
          </p>
        )}
        <div className="flex w-full flex-wrap items-center justify-between gap-4 px-2">
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-400">
            <span>Année en cours : <strong className="font-medium text-slate-300">{currentYear}</strong></span>
            <span className="opacity-50">•</span>
            <span>Objectif actif : <strong className="font-medium text-slate-300">{activeObjective?.name || "Aucun"}</strong></span>
            <span className="opacity-50">•</span>
            <span data-testid={TESTIDS.pilotage.dashboard.lastUpdated}>MAJ : {formatLastUpdated(lastUpdatedAt)}</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 border-white/10 text-xs text-slate-300 hover:bg-white/5 hover:text-white"
            onClick={handleRefresh}
            disabled={loading}
            data-testid={TESTIDS.pilotage.dashboard.refreshButton}
          >
            Rafraîchir
          </Button>
        </div>

        {loading ? (
          <DashboardLoadingSkeleton />
        ) : null}

        {!loading && error ? (
          <AppErrorState
            title="Indicateurs Pilotage indisponibles"
            message={error}
            hint="Les liens rapides restent accessibles pendant l'incident."
            onRetry={handleRefresh}
            testId={TESTIDS.pilotage.dashboard.errorState}
          />
        ) : null}

        {!loading && !error && missions && collaborators ? (
          <div className="w-full min-w-0 max-w-full space-y-6">
            <div role="tablist" aria-label="Blocs du Pilotage" className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid={TESTIDS.PILOTAGE_STICKY_SUMMARY}>
              {DASHBOARD_ANCHORS.map((item, index) => {
                const id = item.href.slice(1);
                const selected = activeSection === id;
                const Icon = item.icon;
                return (
                  <button key={id} id={`${id}-tab`} type="button" role="tab"
                    aria-selected={selected} aria-controls={`${id}-panel`} tabIndex={selected ? 0 : -1}
                    onClick={() => selectSection(id)} data-testid={item.testId}
                    onKeyDown={event => {
                      const offsets = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
                      if (!(event.key in offsets) && !["Home", "End"].includes(event.key)) return;
                      event.preventDefault();
                      const next = event.key === "Home" ? 0 : event.key === "End" ? DASHBOARD_ANCHORS.length - 1 : (index + offsets[event.key] + DASHBOARD_ANCHORS.length) % DASHBOARD_ANCHORS.length;
                      const nextId = DASHBOARD_ANCHORS[next].href.slice(1);
                      selectSection(nextId);
                      document.getElementById(`${nextId}-tab`)?.focus();
                    }}
                    className={`relative min-h-28 min-w-0 rounded-2xl border p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-lenvia-blue ${selected ? "border-lenvia-blue bg-lenvia-blue/10" : "border-white/10 bg-slate-900/60 hover:border-slate-500"}`}>
                    {selected && <span aria-hidden="true" className="absolute right-3 top-3 rounded-full bg-lenvia-blue/15 px-2 py-0.5 text-[10px] font-medium text-lenvia-blue">Ouvert</span>}
                    <Icon className={`mb-2 h-5 w-5 ${selected ? "text-lenvia-blue" : "text-slate-400"}`} aria-hidden="true" />
                    <span className="block break-words text-sm font-semibold text-white">{item.label}</span>
                    <span className="mt-1 hidden text-xs leading-relaxed text-slate-400 sm:block">{item.description}</span>
                  </button>
                );
              })}
            </div>

            <DashboardContent
              activeSection={activeSection}
              rawCockpitData={rawCockpitData}
              financials={financials || normalizeFinancials()}
              missions={missions}
              collaborators={collaborators}
              staffingMetrics={staffingMetrics}
              forecastRevenueData={forecastRevenueData}
              businessAlerts={businessAlerts}
              intercontratData={intercontratData}
              isRh={isRh}
              profitabilityRows={profitabilityRows}
              objectiveRealizedMetrics={objectiveRealizedMetrics}
              pnlData={pnlData}
              currentYear={currentYear}
              realizedRevenueYtdClosed={realizedRevenueYtdClosed}
              objectiveId={activeObjective?.id}
            />
          </div>
        ) : null}

        
      </div>
    </AdminLayout>
  );
};

export default PilotageDashboardPage;
